import React, { ChangeEvent, useContext } from 'react';
import { calculatedKwhSensor, ChartContext } from '../context/ChartContext';
import InfoIcon from '@mui/icons-material/Info';
import dayjs from 'dayjs';
import DateSelector from '../../../components/form/DateSelector';
import { GreyColor, SwitchToggle, TextfieldSelect } from '@Iot-Bee/standard-web-library';
import { Box, FormControlLabel, Tooltip, Typography } from '@mui/material';

const Settings = () => {
  const chartContext = useContext(ChartContext);

  if (!chartContext) {
    throw new Error('Settings must be used within a ChartContextProvider');
  }

  const {
    calibrations,
    loadingGraphData,
    isPowerDevice,
    chartOptions: { sensor, calibration, fromDate, toDate, showDataPoints, accumulate },
    setChartOptions: {
      handleCalibrationChange,
      handleAccumulateToDailyChange,
      handleFromDateChange,
      handleToDateChange,
      handleShowDataPointsChange,
    },
  } = chartContext;
  return (
    <>
      <DateSelector
        label="From date"
        value={fromDate ?? dayjs()}
        onChange={handleFromDateChange}
        disableDayFunction={(date) => {
          return toDate === undefined
            ? false
            : date.isAfter(toDate) &&
                !(date.date() === toDate.date() && date.month() === toDate.month() && date.year() === toDate.year());
        }}
        disabled={loadingGraphData}
      />
      <DateSelector
        label="To date"
        value={toDate ?? dayjs()}
        onChange={handleToDateChange}
        disableDayFunction={(date) => {
          return fromDate === undefined
            ? false
            : date.isBefore(fromDate) &&
                !(
                  date.date() === fromDate.date() &&
                  date.month() === fromDate.month() &&
                  date.year() === fromDate.year()
                );
        }}
        disabled={loadingGraphData}
      />
      <TextfieldSelect
        label="Select calibration"
        options={calibrations.map((item) => item.name)}
        value={calibration !== undefined && calibration.name ? calibration.name : ''}
        onChange={handleCalibrationChange}
        showNone={true}
        disabled={loadingGraphData}
        sx={{ width: '100%', minWidth: '165px' }}
      />
      <FormControlLabel
        sx={{
          textDecoration: 'none',
          fontStyle: 'normal',
          fontWeight: 500,
          fontSize: '14px',
          lineHeight: '120%',
        }}
        control={
          <SwitchToggle
            checked={showDataPoints ?? false}
            onChange={(ev: ChangeEvent<HTMLInputElement>) => {
              handleShowDataPointsChange(ev.target.checked);
            }}
            name="showDataPoints"
            disabled={sensor === calculatedKwhSensor}
          />
        }
        label={
          <Typography
            sx={{
              color: '#111111',
              fontStyle: 'normal',
              fontWeight: 500,
              fontSize: '14px',
              ml: '11px',
            }}
          >
            Show data points
          </Typography>
        }
        disabled={loadingGraphData}
      />
      {isPowerDevice && (
        <FormControlLabel
          sx={{
            textDecoration: 'none',
            fontStyle: 'normal',
            fontWeight: 500,
            fontSize: '14px',
            lineHeight: '120%',
          }}
          control={
            <SwitchToggle
              checked={accumulate}
              onChange={(ev: ChangeEvent<HTMLInputElement>) => {
                handleAccumulateToDailyChange(ev.target.checked);
              }}
              name="accumulate"
            />
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography
                sx={{
                  color: '#111111',
                  fontStyle: 'normal',
                  fontWeight: 500,
                  fontSize: '14px',
                  ml: '11px',
                }}
              >
                Accumulate
              </Typography>
              <Tooltip title="Accumulate kwh data" arrow>
                <InfoIcon sx={{ fontSize: '18px', ml: '5px', color: GreyColor }} />
              </Tooltip>
            </Box>
          }
          disabled={loadingGraphData || sensor !== calculatedKwhSensor}
        />
      )}
    </>
  );
};

export default Settings;
